import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent, merge, Subscription } from 'rxjs';

/** The footer of an `app-dialog`: `<ng-template appDialogFooter>…</ng-template>`. */
@Directive({ selector: '[appDialogFooter]', standalone: true })
export class DialogFooterDirective {}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A modal dialog shown while `visible` is set, opened with the Material dialog
 * service. It is used declaratively: `[(visible)]`, an optional header with the
 * close button, the content and a footer template. Like the other popups of the
 * app it is centered, square and has a transparent shade. When it opens, the
 * first focusable element of the content gets the focus, or else the first one
 * of the footer.
 */
@Component({
  selector: 'app-dialog',
  template: `<ng-template #dialogTemplate>
    @if (showHeader) {
      <div class="app-dialog-header">
        <span class="app-dialog-title">{{ header }}</span>
        @if (closable) {
          <button
            type="button"
            class="app-dialog-close"
            aria-label="Close"
            (click)="close()"
          >
            <i class="app-icon fa-solid fa-xmark"></i>
          </button>
        }
      </div>
    }
    <div class="app-dialog-content"><ng-content></ng-content></div>
    @if (footer) {
      <div class="app-dialog-footer">
        <ng-container [ngTemplateOutlet]="footer"></ng-container>
      </div>
    }
  </ng-template>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgTemplateOutlet],
})
export class DialogComponent implements OnChanges, OnDestroy {
  private dialog = inject(MatDialog);
  private changeDetector = inject(ChangeDetectorRef);

  @Input() visible = false;
  @Input() header = '';
  @Input() showHeader = true;
  @Input() closable = true;
  @Input() modal = true;
  @Input() closeOnEscape = true;
  @Input() dismissableMask = false;
  @Input() styleClass = '';
  /**
   * `sheet`: a side sheet at the right edge in full height, 480 px wide, for
   * editing with the page in view; `modal` decides on the shade.
   */
  @Input() variant: 'dialog' | 'sheet' = 'dialog';
  @Input() style: {
    width?: string;
    maxWidth?: string;
    minWidth?: string;
    maxHeight?: string;
  } = {};
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() opened = new EventEmitter<void>();

  @ViewChild('dialogTemplate', { static: true })
  private dialogTemplate!: TemplateRef<unknown>;
  @ContentChild(DialogFooterDirective, { read: TemplateRef })
  footer?: TemplateRef<unknown>;

  private ref: MatDialogRef<unknown> | null = null;
  private subscriptions = new Subscription();

  ngOnChanges(): void {
    if (this.visible && !this.ref) {
      this.open();
    } else if (!this.visible && this.ref) {
      this.ref.close();
    }
  }

  ngOnDestroy(): void {
    this.ref?.close();
  }

  close() {
    this.ref?.close();
  }

  private open() {
    const sheet = this.variant === 'sheet';
    const ref = this.dialog.open(this.dialogTemplate, {
      panelClass: [
        'app-dialog-panel',
        ...(sheet ? ['app-side-sheet'] : []),
        ...this.styleClass.split(' ').filter(Boolean),
      ],
      ...(sheet
        ? {
            position: { right: '0', top: '0' },
            height: '100%',
            width: this.style.width ?? '480px',
            maxWidth: '100vw',
            maxHeight: '100vh',
          }
        : {}),
      hasBackdrop: this.modal,
      backdropClass: 'app-dialog-backdrop',
      // Escape and the shade are handled here, as the inputs say.
      disableClose: true,
      autoFocus: false,
      restoreFocus: true,
      ...(sheet
        ? {}
        : {
            width: this.style.width,
            minWidth: this.style.minWidth,
            maxWidth: this.style.maxWidth ?? '100vw',
            // 80% of the window unless the style says otherwise.
            maxHeight: this.style.maxHeight ?? '80vh',
          }),
    });
    this.ref = ref;
    // The subscriptions of one opening end when that dialog closes.
    this.subscriptions = new Subscription();
    this.subscriptions.add(
      ref.backdropClick().subscribe(() => this.dismissableMask && ref.close())
    );
    this.subscriptions.add(
      ref.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape' && this.closeOnEscape) {
          ref.close();
        }
      })
    );
    this.subscriptions.add(
      ref.afterOpened().subscribe(() => {
        // The first field of the content, else the first button of the footer.
        const pane = document.getElementById(ref.id);
        const first = (selector: string) =>
          pane?.querySelector<HTMLElement>(`${selector} :is(${FOCUSABLE})`);
        (first('.app-dialog-content') ?? first('.app-dialog-footer'))?.focus();
        this.opened.emit();
        // The footer template belongs to the component that uses the dialog,
        // but it is rendered in the dialog pane: its events mark only the
        // views up from there. Under OnPush that component, whose state the
        // footer buttons change, has to be marked here.
        if (pane) {
          this.subscriptions.add(
            merge(
              fromEvent(pane, 'click'),
              fromEvent(pane, 'keydown')
            ).subscribe(() => this.changeDetector.markForCheck())
          );
        }
      })
    );
    this.subscriptions.add(
      ref.afterClosed().subscribe(() => {
        this.subscriptions.unsubscribe();
        this.ref = null;
        if (this.visible) {
          this.visible = false;
          this.visibleChange.emit(false);
        }
      })
    );
  }
}
